<template>
  <div>
    <BaseHeader />
    <div class="container_wrap basic">
      <BaseTitle :title="pageName" />
      <div class="container guide">
        <div class="event_title">
          <p>{{ serviceInfo.name }}</p>
        </div>
        <LineInfoTable
          subTitle="노선별 출발시간 및 운임 안내"
          :headers="lineInfoHeader"
          :contents="timeFares"
          @openMapInfo="
            (stations, order) => $emit('openMapInfo', stations, order)
          "
        />
        <!-- 행사장 정류장 위치 -->
        <div class="event_place">
          <div class="place_item">
            <div class="sub_title">
              <h3>{{ festaStationTitle }}</h3>
            </div>
            <div class="place_img">
              <Map
                height="300px"
                :zoom="16"
                :station="festaStation"
                :draggable="false"
                :isControlledZoom="false"
              />
            </div>
          </div>
        </div>
        <LineInfoText subTitle="운행일" :content="driveDates" />
        <template v-for="(bookInterval, idx) in bookIntervals" :key="idx">
          <LineInfoText
            :subTitle="`${bookInterval.bookPointName.replace(/ /g, '')} 기간`"
            :content="bookTermText(bookInterval)"
          />
        </template>
        <template v-for="(bookInterval, idx) in bookIntervals" :key="idx">
          <LineInfoText
            :subTitle="`${bookInterval.bookPointName.replace(
              / /g,
              ''
            )} 취소 기한`"
            :content="bookCancelDateText(bookInterval)"
          />
        </template>
        <BaseInfo v-if="bookHelpText" :infoList="bookHelpText" />
        <BaseButton
          :disabled="!isBookInterval() || !driveDates.length"
          name="예약하기"
          @click="nextBookStep()"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  ref,
  computed,
  onBeforeMount,
  onBeforeUnmount,
  getCurrentInstance,
} from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import BaseHeader from "@/components/Base/BaseHeader.vue";
import BaseTitle from "@/components/Base/BaseTitle.vue";
import LineInfoTable from "@/components/LineInfo/LineInfoTable.vue";
import Map from "@/components/Map/Map.vue";
import LineInfoText from "@/components/LineInfo/LineInfoText.vue";
import BaseInfo from "@/components/Base/BaseInfo.vue";
import BaseButton from "@/components/Base/BaseButton.vue";
import { formatBookCancelTerm } from "@/utils/date";
import dayjs from "dayjs";
import "dayjs/locale/ko";
dayjs.locale("ko");

export default {
  name: "LineInfo",
  components: {
    BaseHeader,
    LineInfoTable,
    LineInfoText,
    BaseInfo,
    BaseButton,
    BaseTitle,
    Map,
  },
  props: {
    page: {
      type: String,
    },
    pageName: {
      type: String,
    },
  },
  setup() {
    const { proxy } = getCurrentInstance();

    const lineInfoHeader = computed(() => {
      let header = [{ text: "노선", value: "lineRouteName" }];
      if (serviceInfo.value.forwardBeginTimeDisplayYn) {
        header.push({ text: "행사장행", value: "forwardBeginTimeText" });
      }
      if (serviceInfo.value.backwardBeginTimeDisplayYn) {
        header.push({ text: "귀가행", value: "backwardBeginTimeText" });
      }
      // 왕복운임인 경우 행사장행과 귀가행 운임이 다른 경우 대응이 안되는 문제가 있음
      // serviceInfo.value.fareCaptionRoundTripYn
      // 	? header.push({ text: '왕복운임(원)', value: 'fares' })
      // 	: header.push({ text: '운임(원)', value: 'fares' });
      header.push({ text: "편도운임(원)", value: "fares" });
      return header;
    });

    const router = useRouter();
    const store = useStore();
    const serviceInfo = computed(() => store.state.common.serviceInfo);
    const driveDates = computed(() => store.getters["common/driveDates"]);
    const bookIntervals = computed(() => store.getters["common/bookIntervals"]);
    const bookHelpText = computed(() => store.getters["common/bookHelpText"]);
    const timeFares = computed(() => store.getters["common/timeFares"]);

    // 예약정보 삭제
    store.dispatch("book/CLEAR_LINES");
    // accessToken 삭제
    store.dispatch("member/CLEAR_ACCESS_TOKEN");
    // 예약자 정보 삭제
    store.dispatch("member/CLEAR_USER_INFO");
    if (window.sessionStorage.getItem("payInfo")) {
      window.sessionStorage.removeItem("payInfo");
    }

    const route = useRoute();
    const serviceCode = route.params.serviceCode.toUpperCase();
    const getServiceInfo = async () => {
      if (Object.keys(serviceInfo.value).length === 0) {
        const result = await store.dispatch("common/FETCH_SERVICE_INFO", {
          serviceCode,
        });
        if (result.resultCode !== 0) {
          proxy.$root.$openErrorPopup({
            errorCode: result.resultCode,
            errorTime: result.resultTs,
            errorMessage: result.resultMessage,
          });
          return;
        }
      }
      getFestaStation();
    };
    const festaStation = ref({});
    const festaStationTitle = computed(() => festaStation.value?.name ?? "");
    const getFestaStation = async () => {
      festaStation.value = await store.dispatch("line/FETCH_STATION_POSITION", {
        stationUid: serviceInfo.value.targetStationUid,
      });
    };

    const nowDtm = ref(dayjs().format("YYYYMMDDHHmm"));
    const interval = ref(null);
    const getNowDateTime = () => {
      nowDtm.value = dayjs().format("YYYYMMDDHHmm");
    };
    const isBookInterval = () => {
      interval.value = setInterval(getNowDateTime, 1000);
      if (!bookIntervals.value?.length || !nowDtm.value) {
        return false;
      }
      return !!bookIntervals.value.find((interval) => {
        const bookBeginDtm = dayjs(interval.bookBeginDtm).format(
          "YYYYMMDDHHmm"
        );
        const bookEndDtm = dayjs(interval.bookEndDtm).format("YYYYMMDDHHmm");
        if (nowDtm.value >= bookBeginDtm && nowDtm.value < bookEndDtm) {
          return interval;
        }
      });
    };

    // const isBookableDates = computed(() => bookableDates.value.length);

    // const bookableDates = ref([]);
    // const getDriveDate = async () => {
    // 	// 운행 가능한 날짜 목록(고정노선) 호출 - 목적지행
    // 	let result = await store.dispatch('book/GET_BOOK_STEPS', {
    // 		lineTypeCode: 'LRT-FORWAR',
    // 		lineOnDemandTypeCode: 'LOD-NONE',
    // 	});
    // 	if (result.bookableDates.length) {
    // 		bookableDates.value = result.bookableDates;
    // 		return;
    // 	}
    // 	result = await store.dispatch('book/GET_BOOK_STEPS', {
    // 		lineTypeCode: 'LRT-BACKWA',
    // 		lineOnDemandTypeCode: 'LOD-NONE',
    // 	});
    // 	bookableDates.value = result.bookableDates;
    // };

    const bookTermText = (bookInterval) => {
      return `<span>${bookInterval.bookBeginDtmText}</span><br /><span> ~ ${bookInterval.bookEndDtmText}</span>`;
    };
    const bookCancelDateText = (bookInterval) => {
      if (
        !bookInterval.bookCancelDtm ||
        bookInterval.bookBeginDtm > bookInterval.bookCancelDtm
      ) {
        return `<strong>예약취소 불가</strong>`;
      }
      return `<span>${formatBookCancelTerm(bookInterval.bookCancelDtm)}</span>`;
    };

    const bookSteps = computed(() => store.state.book.bookSteps);
    const nextBookStep = async () => {
      router.push({ name: bookSteps.value?.[0].page });
    };

    onBeforeMount(() => {
      getServiceInfo();
      // getDriveDate();
    });

    onBeforeUnmount(() => {
      clearInterval(interval.value);
    });

    return {
      lineInfoHeader,
      serviceInfo,
      driveDates,
      bookIntervals,
      bookHelpText,
      festaStation,
      festaStationTitle,
      timeFares,
      // isBookableDates,
      // bookCancelTerm,
      isBookInterval,
      bookTermText,
      nextBookStep,
      bookCancelDateText,
    };
  },
};
</script>
